import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import toast from "react-hot-toast";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { AdvancePaymentRequestService } from "services/advance-payment-request";
import { formatVndIntegerInput, parseVndIntegerInput } from "utils";
import { getPrintTemplate } from "./components/printTemplate";

const splitDateParts = (dateText = "") => {
  const [day = "", month = "", year = ""] = String(dateText).split("/");
  return {
    day: day.replace(/\D/g, "").slice(0, 2),
    month: month.replace(/\D/g, "").slice(0, 2),
    year: year.replace(/\D/g, "").slice(0, 4),
  };
};

const hasAnyDatePart = (parts) => Boolean(parts.day || parts.month || parts.year);
const hasAllDatePart = (parts) => Boolean(parts.day && parts.month && parts.year);

const composeDate = (parts) => {
  if (!hasAnyDatePart(parts)) return "";
  const day = parts.day.padStart(2, "0");
  const month = parts.month.padStart(2, "0");
  const year = parts.year;
  return `${day}/${month}/${year}`;
};

const validateDateParts = (parts, label) => {
  if (!hasAnyDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  if (!hasAllDatePart(parts)) throw new Error(`${label} chưa nhập đủ ngày/tháng/năm`);
  const day = Number(parts.day);
  const month = Number(parts.month);
  const year = Number(parts.year);
  if (month < 1 || month > 12) throw new Error(`${label} có tháng không hợp lệ`);
  if (year < 1900 || year > 9999) throw new Error(`${label} có năm không hợp lệ`);
  const maxDays = new Date(year, month, 0).getDate();
  if (day < 1 || day > maxDays) throw new Error(`${label} có ngày không hợp lệ`);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const validateOptionalDateText = (value, label) => {
  const text = String(value || "").trim();
  if (!text) return "";
  const match = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!match) throw new Error(`${label} không đúng định dạng dd/mm/yyyy`);
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const maxDays = new Date(year, month, 0).getDate();
  if (month < 1 || month > 12 || year < 1900 || year > 9999 || day < 1 || day > maxDays) {
    throw new Error(`${label} không hợp lệ`);
  }
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(year)}`;
};

const fmtMoney = (value) => new Intl.NumberFormat("vi-VN").format(Math.round(Number(value) || 0));

const createDetailRow = () => ({ description: "", amount: "" });
const createDocRow = () => ({ documentNumber: "", documentDate: "", amount: "" });

export default function AdvancePaymentRequestDetail() {
  const { id } = useParams();
  const location = useLocation();
  const containView = location.pathname?.includes("/view");
  const navigation = useNavigate();

  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const today = new Date();
  const [documentDateParts, setDocumentDateParts] = useState({
    day: String(today.getDate()).padStart(2, "0"),
    month: String(today.getMonth() + 1).padStart(2, "0"),
    year: String(today.getFullYear()),
  });
  const [header, setHeader] = useState({
    unit: "",
    department: "",
    documentNumber: "",
    debitAccount: "",
    creditAccount: "",
    payerName: "",
    payerDepartmentAddress: "",
  });
  const [details, setDetails] = useState([createDetailRow()]);
  const [previousUnspent, setPreviousUnspent] = useState("");
  const [currentAdvanceDocs, setCurrentAdvanceDocs] = useState([
    createDocRow(),
    createDocRow(),
    createDocRow(),
  ]);
  const [spentDocs, setSpentDocs] = useState([createDocRow(), createDocRow()]);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const response = await AdvancePaymentRequestService.getById(id);
        const data = response.data ? response.data : response;

        setHeader({
          unit: data.unit || "",
          department: data.department || "",
          documentNumber: data.documentNumber || "",
          debitAccount: data.debitAccount || "",
          creditAccount: data.creditAccount || "",
          payerName: data.payerName || "",
          payerDepartmentAddress: data.payerDepartmentAddress || "",
        });

        setDocumentDateParts(splitDateParts(data.documentDate || ""));
        setDetails(
          (data.details || []).length
            ? data.details.map((row) => ({
                description: row.description || "",
                amount: formatVndIntegerInput(row.amount || ""),
              }))
            : [createDetailRow()]
        );

        setPreviousUnspent(formatVndIntegerInput(data.previousUnspent || ""));

        setCurrentAdvanceDocs(
          (data.currentAdvanceDocs || []).length
            ? data.currentAdvanceDocs.map((row) => ({
                documentNumber: row.documentNumber || "",
                documentDate: row.documentDate || "",
                amount: formatVndIntegerInput(row.amount || ""),
              }))
            : [createDocRow(), createDocRow(), createDocRow()]
        );

        setSpentDocs(
          (data.spentDocs || []).length
            ? data.spentDocs.map((row) => ({
                documentNumber: row.documentNumber || "",
                documentDate: row.documentDate || "",
                amount: formatVndIntegerInput(row.amount || ""),
              }))
            : [createDocRow(), createDocRow()]
        );
      } catch (error) {
        toast.error("Không thể tải dữ liệu giấy thanh toán tiền tạm ứng");
      }
    })();
  }, [id]);

  const setHeaderField = (field, value) => {
    setHeader((prev) => ({ ...prev, [field]: value }));
  };

  const setDateField = (field, value) => {
    const max = field === "year" ? 4 : 2;
    const raw = value.replace(/\D/g, "");
    setDocumentDateParts((prev) => ({ ...prev, [field]: field === "year" ? raw.slice(0, max) : raw.slice(-max) }));
  };

  const padDateField = (field) => {
    if (field === "year") return;
    setDocumentDateParts((prev) => {
      if (!prev[field]) return prev;
      return { ...prev, [field]: String(prev[field]).padStart(2, "0") };
    });
  };

  const updateDetail = (index, key, value) => {
    setDetails((prev) =>
      prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row))
    );
  };

  const updateDocRow = (setter, index, key, value) => {
    setter((prev) => prev.map((row, idx) => (idx === index ? { ...row, [key]: value } : row)));
  };

  const addDetailRow = () => setDetails((prev) => [...prev, createDetailRow()]);
  const removeDetailRow = (index) =>
    setDetails((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev));

  const addCurrentAdvanceDoc = () => setCurrentAdvanceDocs((prev) => [...prev, createDocRow()]);
  const removeCurrentAdvanceDoc = (index) =>
    setCurrentAdvanceDocs((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev));

  const addSpentDoc = () => setSpentDocs((prev) => [...prev, createDocRow()]);
  const removeSpentDoc = (index) =>
    setSpentDocs((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== index) : prev));

  const previousUnspentValue = useMemo(() => parseVndIntegerInput(previousUnspent), [previousUnspent]);
  const currentAdvanceTotal = useMemo(
    () => currentAdvanceDocs.reduce((sum, row) => sum + parseVndIntegerInput(row.amount), 0),
    [currentAdvanceDocs]
  );
  const totalAdvance = useMemo(() => previousUnspentValue + currentAdvanceTotal, [previousUnspentValue, currentAdvanceTotal]);
  const totalSpent = useMemo(
    () => spentDocs.reduce((sum, row) => sum + parseVndIntegerInput(row.amount), 0),
    [spentDocs]
  );
  const diffUnspent = useMemo(() => Math.max(totalAdvance - totalSpent, 0), [totalAdvance, totalSpent]);
  const diffOverspent = useMemo(() => Math.max(totalSpent - totalAdvance, 0), [totalAdvance, totalSpent]);

  const buildPayload = () => {
    const unit = String(header.unit || "").trim();
    const department = String(header.department || "").trim();
    const documentNumber = String(header.documentNumber || "").trim();
    const documentDate = validateDateParts(documentDateParts, "Ngày chứng từ");
    const debitAccount = String(header.debitAccount || "").trim();
    const creditAccount = String(header.creditAccount || "").trim();
    const payerName = String(header.payerName || "").trim();
    const payerDepartmentAddress = String(header.payerDepartmentAddress || "").trim();

    if (!unit) throw new Error("Đơn vị không được để trống");
    if (!department) throw new Error("Bộ phận không được để trống");
    if (!documentNumber) throw new Error("Số chứng từ không được để trống");
    if (!payerName) throw new Error("Họ tên người thanh toán không được để trống");
    if (!payerDepartmentAddress) throw new Error("Bộ phận (hoặc địa chỉ) không được để trống");

    const normalizedDetails = details
      .map((row) => ({
        description: String(row.description || "").trim(),
        amount: parseVndIntegerInput(row.amount),
      }))
      .filter((row) => row.description || row.amount > 0);

    for (const [idx, row] of normalizedDetails.entries()) {
      if (!row.description && row.amount > 0) {
        throw new Error(`Dòng ${idx + 1}: Diễn giải không được để trống`);
      }
    }

    const normalizeDocs = (rows, label) =>
      rows
        .map((row, idx) => {
          const documentDateValue = validateOptionalDateText(row.documentDate, `${label} dòng ${idx + 1}`);
          return {
            documentNumber: String(row.documentNumber || "").trim(),
            documentDate: documentDateValue,
            amount: parseVndIntegerInput(row.amount),
          };
        })
        .filter((row) => row.documentNumber || row.documentDate || row.amount > 0);

    const currentAdvanceDocsPayload = normalizeDocs(currentAdvanceDocs, "Ngày phiếu chi");
    const spentDocsPayload = normalizeDocs(spentDocs, "Ngày chứng từ");

    return {
      unit,
      department,
      documentDate,
      documentNumber,
      debitAccount,
      creditAccount,
      payerName,
      payerDepartmentAddress,
      details: normalizedDetails,
      previousUnspent: previousUnspentValue,
      currentAdvanceDocs: currentAdvanceDocsPayload,
      spentDocs: spentDocsPayload,
      totalAdvance,
      totalSpent,
      diffUnspent,
      diffOverspent,
    };
  };

  const submit = async () => {
    try {
      setLoadingSubmit(true);
      const payload = buildPayload();
      if (!id) {
        await AdvancePaymentRequestService.create(payload);
        toast.success("Tạo giấy thanh toán tiền tạm ứng thành công");
      } else {
        await AdvancePaymentRequestService.update(id, payload);
        toast.success("Cập nhật giấy thanh toán tiền tạm ứng thành công");
      }
      navigation("/templates/advance-payment-request");
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Lưu thất bại");
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handlePrint = () => {
    const htmlContent = getPrintTemplate({
      data: {
        ...header,
        documentDate: composeDate(documentDateParts),
        details: details.map((row) => ({
          description: row.description,
          amount: parseVndIntegerInput(row.amount),
        })),
        previousUnspent: previousUnspentValue,
        currentAdvanceDocs: currentAdvanceDocs.map((row) => ({
          documentNumber: row.documentNumber,
          documentDate: row.documentDate,
          amount: parseVndIntegerInput(row.amount),
        })),
        spentDocs: spentDocs.map((row) => ({
          documentNumber: row.documentNumber,
          documentDate: row.documentDate,
          amount: parseVndIntegerInput(row.amount),
        })),
        totalAdvance,
        totalSpent,
        diffUnspent,
        diffOverspent,
      },
    });

    const printWindow = window.open("", "_blank");
    printWindow.document.open();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  };

  const inputStyle = {
    border: "none",
    borderBottom: "1px dotted #000",
    outline: "none",
    fontSize: 16,
    fontFamily: "inherit",
    background: "transparent",
    minWidth: 20,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox
                p={2}
                style={{
                  fontFamily: '"Times New Roman", Times, serif',
                  fontSize: 16,
                  color: "#000",
                  backgroundColor: "#fff",
                  padding: 20,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <div>
                    <div>
                      <b>Đơn vị:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={header.unit}
                        onChange={(event) => setHeaderField("unit", event.target.value)}
                      />
                    </div>
                    <div>
                      <b>Bộ phận:</b>{" "}
                      <input
                        disabled={containView}
                        style={{ ...inputStyle, width: 220 }}
                        value={header.department}
                        onChange={(event) => setHeaderField("department", event.target.value)}
                      />
                    </div>
                  </div>
                  <div style={{ textAlign: "center" }}>
                    <div>
                      <b>Mẫu số 04 - TT</b>
                    </div>
                    <div style={{ fontStyle: "italic", fontSize: 13 }}>
                      (Kèm theo Thông tư số 99/2025/TT-BTC
                      <br />
                      ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)
                    </div>
                  </div>
                </div>

                <h2 style={{ textAlign: "center", margin: "8px 0 4px", textTransform: "uppercase", fontWeight: 700 }}>
                  GIẤY THANH TOÁN TIỀN TẠM ỨNG
                </h2>

                <div style={{ textAlign: "center", marginBottom: 8 }}>
                  Ngày
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.day}
                    onChange={(event) => setDateField("day", event.target.value)}
                    onBlur={() => padDateField("day")}
                  />
                  tháng
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 34, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.month}
                    onChange={(event) => setDateField("month", event.target.value)}
                    onBlur={() => padDateField("month")}
                  />
                  năm
                  <input
                    disabled={containView}
                    style={{ ...inputStyle, width: 48, textAlign: "center", margin: "0 4px" }}
                    value={documentDateParts.year}
                    onChange={(event) => setDateField("year", event.target.value)}
                  />
                </div>

                <div style={{ textAlign: "right", lineHeight: 1.7 }}>
                  <div>
                    Số:{" "}
                    <input
                      disabled={containView}
                      style={{ ...inputStyle, width: 160 }}
                      value={header.documentNumber}
                      onChange={(event) => setHeaderField("documentNumber", event.target.value)}
                    />
                  </div>
                  <div>
                    Nợ:{" "}
                    <input
                      disabled={containView}
                      style={{ ...inputStyle, width: 160 }}
                      value={header.debitAccount}
                      onChange={(event) => setHeaderField("debitAccount", event.target.value)}
                    />
                  </div>
                  <div>
                    Có:{" "}
                    <input
                      disabled={containView}
                      style={{ ...inputStyle, width: 160 }}
                      value={header.creditAccount}
                      onChange={(event) => setHeaderField("creditAccount", event.target.value)}
                    />
                  </div>
                </div>

                <div style={{ marginTop: 12, lineHeight: 1.8 }}>
                  <div>- Họ và tên người thanh toán:{" "}
                    <input
                      disabled={containView}
                      style={{ ...inputStyle, width: 420 }}
                      value={header.payerName}
                      onChange={(event) => setHeaderField("payerName", event.target.value)}
                    />
                  </div>
                  <div>- Bộ phận (hoặc địa chỉ):{" "}
                    <input
                      disabled={containView}
                      style={{ ...inputStyle, width: 420 }}
                      value={header.payerDepartmentAddress}
                      onChange={(event) => setHeaderField("payerDepartmentAddress", event.target.value)}
                    />
                  </div>
                  <div>- Số tiền tạm ứng được thanh toán theo bảng dưới đây:</div>
                </div>

                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 6 }}>
                  <thead>
                    <tr>
                      <th style={{ border: "1px solid #000", padding: 6, width: "65%" }}>Diễn giải</th>
                      <th style={{ border: "1px solid #000", padding: 6 }}>Số tiền</th>
                    </tr>
                    <tr>
                      <th style={{ border: "1px solid #000", padding: 6, textAlign: "center" }}>A</th>
                      <th style={{ border: "1px solid #000", padding: 6, textAlign: "center" }}>1</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}><b>I. Số tiền tạm ứng</b></td>
                      <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>{fmtMoney(totalAdvance)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}>1. Số tạm ứng các kỳ trước chưa chi hết</td>
                      <td style={{ border: "1px solid #000", padding: 6 }}>
                        <input
                          disabled={containView}
                          style={{ ...inputStyle, width: "100%", textAlign: "right" }}
                          value={previousUnspent}
                          onChange={(event) => setPreviousUnspent(formatVndIntegerInput(event.target.value))}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}>2. Số tạm ứng kỳ này</td>
                      <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>{fmtMoney(currentAdvanceTotal)}</td>
                    </tr>
                    {currentAdvanceDocs.map((row, idx) => (
                      <tr key={`ca-${idx}`}>
                        <td style={{ border: "1px solid #000", padding: 6 }}>
                          - Phiếu chi số{" "}
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: 120 }}
                            value={row.documentNumber}
                            onChange={(event) => updateDocRow(setCurrentAdvanceDocs, idx, "documentNumber", event.target.value)}
                          />
                          {" "}ngày{" "}
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: 120 }}
                            value={row.documentDate}
                            onChange={(event) => updateDocRow(setCurrentAdvanceDocs, idx, "documentDate", event.target.value)}
                          />
                        </td>
                        <td style={{ border: "1px solid #000", padding: 6 }}>
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: "100%", textAlign: "right" }}
                            value={row.amount}
                            onChange={(event) => updateDocRow(setCurrentAdvanceDocs, idx, "amount", formatVndIntegerInput(event.target.value))}
                          />
                        </td>
                      </tr>
                    ))}
                    {!containView && (
                      <tr>
                        <td style={{ border: "1px solid #000", padding: 6 }} colSpan={2}>
                          <SoftBox display="flex" gap={1}>
                            <SoftButton variant="outlined" color="info" size="small" onClick={addCurrentAdvanceDoc}>
                              Thêm phiếu chi
                            </SoftButton>
                            <SoftButton
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => removeCurrentAdvanceDoc(currentAdvanceDocs.length - 1)}
                            >
                              Xóa phiếu chi
                            </SoftButton>
                          </SoftBox>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}><b>II. Số tiền đã chi</b></td>
                      <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>{fmtMoney(totalSpent)}</td>
                    </tr>
                    {spentDocs.map((row, idx) => (
                      <tr key={`sp-${idx}`}>
                        <td style={{ border: "1px solid #000", padding: 6 }}>
                          {idx + 1}. Chứng từ số{" "}
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: 120 }}
                            value={row.documentNumber}
                            onChange={(event) => updateDocRow(setSpentDocs, idx, "documentNumber", event.target.value)}
                          />
                          {" "}ngày{" "}
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: 120 }}
                            value={row.documentDate}
                            onChange={(event) => updateDocRow(setSpentDocs, idx, "documentDate", event.target.value)}
                          />
                        </td>
                        <td style={{ border: "1px solid #000", padding: 6 }}>
                          <input
                            disabled={containView}
                            style={{ ...inputStyle, width: "100%", textAlign: "right" }}
                            value={row.amount}
                            onChange={(event) => updateDocRow(setSpentDocs, idx, "amount", formatVndIntegerInput(event.target.value))}
                          />
                        </td>
                      </tr>
                    ))}
                    {!containView && (
                      <tr>
                        <td style={{ border: "1px solid #000", padding: 6 }} colSpan={2}>
                          <SoftBox display="flex" gap={1}>
                            <SoftButton variant="outlined" color="info" size="small" onClick={addSpentDoc}>
                              Thêm chứng từ
                            </SoftButton>
                            <SoftButton
                              variant="outlined"
                              color="error"
                              size="small"
                              onClick={() => removeSpentDoc(spentDocs.length - 1)}
                            >
                              Xóa chứng từ
                            </SoftButton>
                          </SoftBox>
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}><b>III. Chênh lệch</b></td>
                      <td style={{ border: "1px solid #000", padding: 6 }}></td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}>1. Số tạm ứng chi không hết (I - II)</td>
                      <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>{fmtMoney(diffUnspent)}</td>
                    </tr>
                    <tr>
                      <td style={{ border: "1px solid #000", padding: 6 }}>2. Chi quá số tạm ứng (II - I)</td>
                      <td style={{ border: "1px solid #000", padding: 6, textAlign: "right" }}>{fmtMoney(diffOverspent)}</td>
                    </tr>
                  </tbody>
                </table>


                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="outlined" color="secondary" onClick={() => navigation("/templates/advance-payment-request")}>TRỞ VỀ</SoftButton>
                  <SoftBox display="flex" gap={1}>
                    {containView && (
                      <SoftButton variant="outlined" color="info" onClick={handlePrint}>
                        IN
                      </SoftButton>
                    )}
                    {!containView && (
                      <SoftButton variant="gradient" color="dark" onClick={submit} disabled={loadingSubmit}>
                        {loadingSubmit ? "ĐANG LƯU..." : id ? "CẬP NHẬT" : "TẠO"}
                      </SoftButton>
                    )}
                  </SoftBox>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}
